import React from 'react';
import { Stack, Input, InputLabel } from '@ltvco/refresh-lib/theme';
import { UseFormRegister, FieldErrors } from 'react-hook-form';

interface TokenExFormData {
  email: string;
  first_name: string;
  last_name: string;
  postal_code: string;
  expiration_month: string;
  expiration_year: string;
  expiration_date: string;
  verification_number: string;
  card_number: string;
  tokenex_token: string;
  tokenex_input: string;
  [key: string]: string;
}

interface TokenExFormFieldsProps {
  register: UseFormRegister<TokenExFormData>;
  errors: FieldErrors<TokenExFormData>;
  tokenExLabelRef: React.RefObject<HTMLLabelElement>;
  tokenExWrapperRef: React.RefObject<HTMLDivElement>;
  iframeInputInvalid: boolean;
}

export const TokenExFormFields: React.FC<TokenExFormFieldsProps> = ({
  register,
  errors,
  tokenExLabelRef,
  tokenExWrapperRef,
  iframeInputInvalid,
}) => {
  return (
    <Stack spacing={2} mb={2}>
      <Input
        label="Email Address"
        fullWidth
        {...register('email', {
          required: true,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Please enter a valid email address.',
          },
        })}
        error={Boolean(errors.email)}
        helperText={errors.email && 'Please enter a valid email address.'}
        sx={{ mb: 2 }}
      />

      <Stack direction="row" spacing={2}>
        <Input
          label="First Name"
          fullWidth
          {...register('first_name', {
            required: 'Please enter your first name.',
          })}
          error={Boolean(errors.first_name)}
          helperText={errors.first_name && 'Please enter your first name.'}
        />
        <Input
          label="Last Name"
          fullWidth
          {...register('last_name', {
            required: 'Please enter your last name.',
          })}
          error={Boolean(errors.last_name)}
          helperText={errors.last_name && 'Please enter your last name.'}
        />
      </Stack>

      {/* Card Number Field */}
      <Stack
        ref={tokenExWrapperRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '56px',
          border: '1px solid',
          borderColor: iframeInputInvalid ? 'error.main' : 'divider',
          borderRadius: '4px',
          backgroundColor: 'background.paper',
          '&:hover': {
            borderColor: iframeInputInvalid ? 'error.main' : 'primary.main',
          },
          '&:focus-within': {
            borderColor: iframeInputInvalid ? 'error.main' : 'primary.main',
            borderWidth: '2px',
          },
          '&.error': {
            borderColor: 'error.main',
          },
        }}
      >
        <InputLabel
          ref={tokenExLabelRef}
          htmlFor="tokenex-iframe-target"
          style={{
            position: 'absolute',
            top: '-8px',
            left: '12px',
            backgroundColor: 'white',
            padding: '0 4px',
            fontSize: '12px',
            color: iframeInputInvalid ? '#d32f2f' : '#666',
            zIndex: 1,
          }}
        >
          Card Number
        </InputLabel>
        <div
          id="tokenex-iframe-target"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <Input
          label="Expiration (MM/YY)"
          fullWidth
          {...register('expiration_date', {
            required: true,
            pattern: {
              value: /^(0?[1-9]|1[0-2])\/(\d{2}|\d{4})$/,
              message: 'Use MM/YY or MM/YYYY format',
            },
          })}
          error={Boolean(errors.expiration_date)}
          helperText={errors.expiration_date && 'Use MM/YY or MM/YYYY format.'}
        />
        <Input
          label="CVV"
          fullWidth
          {...register('verification_number', {
            required: true,
            minLength: 3,
            maxLength: 4,
            pattern: /^\d*\.?\d*$/,
          })}
          error={Boolean(errors.verification_number)}
          helperText={
            errors.verification_number &&
            'Please enter a valid verification number.'
          }
        />
      </Stack>

      <Input
        label="ZIP Code"
        fullWidth
        {...register('postal_code', {
          required: true,
          maxLength: 5,
          minLength: 5,
          pattern: /^\d*\.?\d*$/,
        })}
        error={Boolean(errors.postal_code)}
        helperText={errors.postal_code && 'Please enter a valid zip code.'}
        sx={{ mb: 2 }}
      />
    </Stack>
  );
};
